import React, { useEffect, useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import Helmet from 'react-helmet';
import { gridSize } from '@atlaskit/theme';

import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import { isMobileOidc } from '../utilities/oidc/oidc';
import Account from '../components/Account';
import messages from './LogoutPage.messages';
import Bull from '../components/BulletSeparator';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { SectionError } from '../components/FormError';
import { useLocation, useSearchParams } from 'react-router-dom';
import { doLogout } from '../actions/session-actions';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import Heading from '@atlaskit/heading';

const logoutErrorCodes = ['session_refresh_not_supported', 'association_not_supported'] as const;

type LogoutErrorCode = (typeof logoutErrorCodes)[number];

const ERROR_CODE_MESSAGES: Record<LogoutErrorCode, MessageDescriptor> = {
  session_refresh_not_supported: messages.logoutRequiredError,
  association_not_supported: messages.logoutRequiredError,
};

const useErrorCodeInQueryParams = (): LogoutErrorCode | undefined => {
  const [searchParams] = useSearchParams();
  const errorCodeParam = searchParams.get('errorCode');
  return logoutErrorCodes.find(code => code === errorCodeParam);
};

const AccountWrapper = styled.div`
  > * {
    padding-bottom: ${gridSize()}px !important;
    border-bottom: none !important;
  }
`;

const Form = styled.form`
  margin-top: 0;
`;

const ButtonContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const pageId = 'logoutPage';

export async function logoutLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/logout/state', request, {
    capabilities: ['capability-group--logout'],
  });
}

export const LogoutPage = () => {
  const autoSubmit = useAppSelector(state => state.logout.autoSubmit);
  return autoSubmit ? <LogoutAutoSubmit /> : <LogoutConfirmationPage />;
};

const LogoutAutoSubmit = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(doLogout());
  }, [dispatch]);

  // When the browser loads the logout page the user will see the spinner on that page so we don't
  // want to show anything on this page, not even a spinner, to make the redirect more transparent.
  return null;
};

const ErrorMessage = ({
  errorCode,
  formError,
}: {
  errorCode: LogoutErrorCode | undefined;
  formError: string | undefined;
}) => {
  const intl = useIntl();

  if (formError) {
    return <SectionError>{intl.formatMessage(messages.logoutFormError)}</SectionError>;
  }
  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    return <SectionError>{intl.formatMessage(ERROR_CODE_MESSAGES[errorCode])}</SectionError>;
  }
  return null;
};

const LogoutConfirmationPage = () => {
  const { isEmbedded, oidcContext } = useAppSelector(state => state.microbranding);
  const { userData, formError } = useAppSelector(state => state.logout);

  const location = useLocation();
  const intl = useIntl();
  const analyticsClient = useAnalyticsClient();

  const errorCode = useErrorCodeInQueryParams();

  usePageViewedEvent(pageId, {
    isAuthenticatedUser: !!userData,
  });

  const buildHeader = () => (
    <Heading as="h1" size="small">
      {!userData || userData.associatedSessions.length === 0 ? (
        <FormattedMessage {...messages.logoutTitle} />
      ) : (
        <FormattedMessage {...messages.logoutTitleMultiple} />
      )}
    </Heading>
  );

  const buildFooter = () => {
    return isEmbedded ? undefined : (
      <ul className="fine-print">
        <li>
          <PrivacyPolicyLink isMobileLink={isMobileOidc(oidcContext)} />
        </li>
        <Bull />
        <li>
          <UserNoticeLink isMobileLink={isMobileOidc(oidcContext)} />
        </li>
      </ul>
    );
  };

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage(messages.logoutTitle)}</title>
      </Helmet>
      <WhiteboxPage
        pageId={pageId}
        header={buildHeader()}
        footer={buildFooter()}
        whiteboxFooter={
          errorCode ? undefined : (
            <a
              href={'/login/select-account' + location.search}
              onClick={() => {
                analyticsClient.linkClickedEvent(pageId, 'selectAccount');
              }}>
              <FormattedMessage {...messages.switchAccount} />
            </a>
          )
        }>
        <ErrorMessage errorCode={errorCode} formError={formError} />
        {!!userData && userData.associatedSessions.length === 0 && (
          <AccountWrapper>
            <Account
              primary={true}
              avatarUrl={userData.avatarUrl}
              displayName={userData.displayName}
              email={userData.email}
              isolatedCloudLabel={userData.icLabel}
            />
          </AccountWrapper>
        )}
        <LogoutForm />
      </WhiteboxPage>
    </>
  );
};

const LogoutForm = () => {
  const userData = useAppSelector(state => state.logout.userData);
  const dispatch = useAppDispatch();

  const analyticsClient = useAnalyticsClient();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Form
      onSubmit={event => {
        event.preventDefault();
        setSubmitting(true);
        analyticsClient.formSubmittedEvent(pageId, 'logout');
        dispatch(doLogout());
      }}
      data-testid="logout-form">
      <ButtonContainer>
        <TallButton
          appearance="primary"
          id="logout-submit"
          type="submit"
          isFullWidth
          isLoading={submitting}
          testId="logout-button">
          {!userData || userData.associatedSessions.length === 0 ? (
            <FormattedMessage {...messages.logoutConfirm} />
          ) : (
            <FormattedMessage {...messages.logoutConfirmMultiple} />
          )}
        </TallButton>
      </ButtonContainer>
    </Form>
  );
};

export default LogoutPage;
